import type { ReactNode } from 'react';
import { AlertCircle, FileText, Info, TriangleAlert } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

type StatusModalVariant = 'log' | 'info' | 'error' | 'warning';

const variantStyles: Record<
  StatusModalVariant,
  { icon: typeof FileText; accentClass: string; iconWrapClass: string }
> = {
  log: {
    icon: FileText,
    accentClass: 'text-muted-foreground',
    iconWrapClass: 'bg-muted text-muted-foreground',
  },
  info: {
    icon: Info,
    accentClass: 'text-info-muted-foreground',
    iconWrapClass: 'bg-info-muted text-info-muted-foreground',
  },
  error: {
    icon: AlertCircle,
    accentClass: 'text-error-muted-foreground',
    iconWrapClass: 'bg-error-muted text-error-muted-foreground',
  },
  warning: {
    icon: TriangleAlert,
    accentClass: 'text-warning-muted-foreground',
    iconWrapClass: 'bg-warning-muted text-warning-muted-foreground',
  },
};

export interface StatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  details?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
  variant: StatusModalVariant;
}

export function StatusModal({
  open,
  onOpenChange,
  title,
  description,
  details,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  showCancel = true,
  variant,
}: StatusModalProps) {
  const style = variantStyles[variant];
  const Icon = style.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-130">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-3">
            <span
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${style.iconWrapClass}`}
            >
              <Icon size={18} />
            </span>
            <DialogTitle className={style.accentClass}>{title}</DialogTitle>
          </div>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {details ? (
          <div className="rounded-lg border border-border-subtle bg-bg-main px-3 py-2 text-sm text-text-main">
            {details}
          </div>
        ) : null}

        <DialogFooter>
          {showCancel ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onCancel?.();
                onOpenChange(false);
              }}
            >
              {cancelText}
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={() => {
              onConfirm?.();
              onOpenChange(false);
            }}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type ModalPropsWithoutVariant = Omit<StatusModalProps, 'variant'>;

export function LogModal(props: ModalPropsWithoutVariant) {
  return <StatusModal {...props} variant="log" />;
}

export function InfoModal(props: ModalPropsWithoutVariant) {
  return <StatusModal {...props} variant="info" />;
}

export function ErrorModal(props: ModalPropsWithoutVariant) {
  return <StatusModal {...props} variant="error" />;
}

export function WarningModal(props: ModalPropsWithoutVariant) {
  return <StatusModal {...props} variant="warning" />;
}
